/**
 * The small Markdown subset assistant answers use: paragraphs, headings, bullet and numbered
 * lists, **bold**. Everything else stays literal text. References (`{building:K}` …) are
 * parsed inside each inline run.
 */
import { parseAnswerReferences, type AnswerSegment } from './assistantReferences';

export type InlineSegment = AnswerSegment | { type: 'bold'; children: AnswerSegment[] };

export type AnswerBlock =
    | { type: 'heading'; inline: InlineSegment[] }
    | { type: 'paragraph'; lines: InlineSegment[][] }
    | { type: 'list'; ordered: boolean; items: InlineSegment[][] };

const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;
const HEADING = /^#{1,4}\s+(.*)$/;

export function parseInline(text: string): InlineSegment[] {
    const segments: InlineSegment[] = [];
    const parts = text.split(/\*\*(.+?)\*\*/g);
    parts.forEach((part, i) => {
        if (!part) return;
        if (i % 2 === 1) segments.push({ type: 'bold', children: parseAnswerReferences(part) });
        else segments.push(...parseAnswerReferences(part));
    });
    return segments;
}

export function parseAnswerMarkdown(text: string): AnswerBlock[] {
    const blocks: AnswerBlock[] = [];
    let paragraph: InlineSegment[][] | null = null;
    let list: { ordered: boolean; items: InlineSegment[][] } | null = null;

    const flush = () => {
        if (paragraph) blocks.push({ type: 'paragraph', lines: paragraph });
        if (list) blocks.push({ type: 'list', ordered: list.ordered, items: list.items });
        paragraph = null;
        list = null;
    };

    for (const line of text.replace(/\r\n/g, '\n').split('\n')) {
        if (!line.trim()) {
            flush();
            continue;
        }
        const heading = HEADING.exec(line);
        const bullet = BULLET.exec(line);
        const numbered = bullet ? null : NUMBERED.exec(line);
        if (heading) {
            flush();
            blocks.push({ type: 'heading', inline: parseInline(heading[1]) });
        } else if (bullet || numbered) {
            const ordered = !!numbered;
            const content = (bullet ?? numbered)![1];
            if (paragraph || (list && list.ordered !== ordered)) flush();
            if (!list) list = { ordered, items: [] };
            list.items.push(parseInline(content));
        } else {
            if (list) flush();
            if (!paragraph) paragraph = [];
            paragraph.push(parseInline(line.trim()));
        }
    }
    flush();
    return blocks;
}
